import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from '@c2fo/liquidity';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useTurnOffParticipation } from '@/enterprise/data/useTurnOffParticipation';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useRestrictions from '@/utils/useRestrictions';
import QueryBoundaries from '../../shared/components/QueryBoundaries';
import { OfferModalWithContext } from '../features/offerModal/OfferModalContext';
import Skeleton from './Skeleton';

interface EarlyMarketProcessingBannerProps {
  takerMarket: TakerMarket;
}

/**
 * Banner component that displays when a market is undergoing early processing
 * Shows a warning message and provides an action to turn off participation
 */
const EarlyMarketProcessingComponent: React.FC<EarlyMarketProcessingBannerProps> = ({ takerMarket }) => {
  const { t } = useTranslation();
  const { getRestrictions } = useRestrictions();
  const { canEditOffers } = getRestrictions([takerMarket]);
  const { turnOffParticipation, isLoading } = useTurnOffParticipation(takerMarket);
  const navigate = useNavigate();
  const isExpressAccept = takerMarket.offerConfig.offerType === 'EXPRESS_ACCEPT';

  // An express-accept offer can have all invoices removed from clear, making isManuallyAwarded false even though the offer is still active
  if (!takerMarket.offerConfig.isManuallyAwarded && !takerMarket.offerConfig.isActiveExpressAccept) {
    return null;
  }

  const disableOffer = async () => {
    try {
      await turnOffParticipation();
      navigate(
        asEnterpriseLink(
          `customers/${takerMarket.makerOrganizationUuid}/markets/${takerMarket.marketUuid}/division/${takerMarket.takerDivisionId}/invoices/eligible`
        )
      );
    } catch {
      // error snackbar handled by turnOffParticipation
    }
  };

  if (isExpressAccept) {
    return (
      <Alert
        description={t('core.expressAcceptProcessingDescription')}
        full
        title={t('core.expressMarketProcessingTitle')}
        type="success"
        variant="outlined"
        action={
          canEditOffers
            ? {
                onClick: disableOffer,
                text: t('core.turnParticipationOff'),
                loading: isLoading,
              }
            : undefined
        }
      />
    );
  }

  return (
    <Alert
      description={t('core.marketIsEarlyProcessingPopover')}
      full
      title={t('core.earlyMarketProcessingTitle')}
      type="warning"
      variant="outlined"
      action={
        !isExpressAccept && canEditOffers
          ? {
              onClick: disableOffer,
              text: t('core.turnParticipationOff'),
              loading: isLoading,
            }
          : undefined
      }
    />
  );
};

const EarlyMarketProcessingBanner = (props: { takerMarket: TakerMarket }) => {
  return (
    <OfferModalWithContext>
      <QueryBoundaries LoadingComponent={() => <Skeleton className="h-40" />}>
        <EarlyMarketProcessingComponent {...props} />
      </QueryBoundaries>
    </OfferModalWithContext>
  );
};

export default EarlyMarketProcessingBanner;
